import Skeleton from "@/components/Skeleton/Skeleton";
import { VisualizationProps } from "@/components/visualizations/Visualization";
import { Tooltip } from "antd";
import _ from "lodash";
import NoDataPoints from "../NoDataPoints/NoDataPoints";
import useVisualization from "../useVisualization";
import styles from "./SingleBarChart.module.scss";

export type SingleBarChartData = { label: string; value: number }[];

export interface SingleBarChartProps extends VisualizationProps {
  data: SingleBarChartData;
}

export default function SingleBarChart(props: SingleBarChartProps) {
  const { data, loading } = props;
  const { formatLabel, colorPalette, fontColorPalette } = useVisualization(props);

  const total = data.map((row) => row.value).reduce((prev, curr) => curr + prev, 0);

  const percentageLabel = (value: number) => {
    return `${total === 0 ? 0 : _.round((value / total) * 100, 2)}%`;
  };

  return (
    <div className={styles.SingleBarChart}>
      <div className={styles.legend}>
        {loading
          ? [...Array(5)].map((_, i) => (
              <div
                key={i}
                className={styles["legend-row"]}
              >
                <Skeleton style={{ width: 80, height: 14, marginBottom: 4 }} />
                <Skeleton style={{ width: 40, height: 14 }} />
              </div>
            ))
          : data.map((row, index) => {
              return (
                <div
                  key={"legend" + index}
                  className={styles["legend-row"]}
                >
                  <div className={styles["legend-header"]}>
                    <span
                      className={styles["legend-color"]}
                      style={{ background: colorPalette[index % colorPalette.length] }}
                    ></span>
                    <span className={styles["legend-label"]}>{row.label}</span>
                  </div>
                  <div className={styles["legend-values"]}>
                    {percentageLabel(row.value)} | {formatLabel(row.value)}
                  </div>
                </div>
              );
            })}
      </div>
      <div className={styles.chart}>
        {loading ? (
          <Skeleton
            style={{
              height: "40px",
              width: "100%",
            }}
          />
        ) : data.length === 0 ? (
          <NoDataPoints />
        ) : (
          <div className={styles["bar-container"]}>
            {data.map(
              (row, index) =>
                row.value > 0 && (
                  <Tooltip
                    title={
                      <div>
                        {row.label}
                        <br />
                        {props.format?.(row.value) || row.value} ({percentageLabel(row.value)})
                      </div>
                    }
                    key={row.label}
                  >
                    <div
                      className={styles.bar}
                      style={{
                        background: colorPalette[index % colorPalette.length],
                        width: percentageLabel(row.value),
                        color: fontColorPalette[index % fontColorPalette.length],
                      }}
                    >
                      {percentageLabel(row.value)}
                    </div>
                  </Tooltip>
                )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
